/**
 * Commission display helpers — SINGLE SOURCE OF TRUTH for the frontend.
 *
 * The rate is no longer derivable from sales: everyone earns BASE_RATE (10%)
 * and only the top 5 of a month earn TOP_PERFORMER_RATE (15%), permanently.
 * Ranking happens on the server, so the API tells us the rate — these helpers
 * take that stored rate and only fall back to 10% when it's missing.
 *
 * Keep in sync with: api/lib/commission.js
 */

export const BASE_RATE = 0.1;
export const TOP_PERFORMER_RATE = 0.15;

// Rate the API reported for a coupon/month, or the 10% default when absent
// (e.g. a month the cron hasn't calculated yet).
export const getCommissionRate = (storedRate?: number | null): number =>
  typeof storedRate === "number" && storedRate > 0 ? storedRate : BASE_RATE;

// Get commission percentage as string for display
export const getCommissionPercentage = (storedRate?: number | null): string => {
  const percent = getCommissionRate(storedRate) * 100;
  return `${Number.isInteger(percent) ? percent : Number(percent.toFixed(2))}%`;
};

// Label shown next to the commission amount
export const getTierName = (storedRate?: number | null): string =>
  getCommissionRate(storedRate) >= TOP_PERFORMER_RATE ? "Top 5" : "Comissão padrão";

export const isTopPerformerRate = (storedRate?: number | null): boolean =>
  getCommissionRate(storedRate) >= TOP_PERFORMER_RATE;

// Calculate final commission amount
export const calculateCommission = (
  totalSales: number,
  shippingFees: number,
  storedRate?: number | null
): number => Math.max(0, (totalSales - shippingFees) * getCommissionRate(storedRate));

// Format currency for Brazilian Real
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};
